'use client'

import React, { useCallback, useMemo, useState } from 'react'
import { Check, X, Pencil, HelpCircle, Info, Copy } from 'lucide-react'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate } from '../ProcessedEntry/DomainEntry/processedTypes'
import type { Catalog, CatalogItem, TranslationData } from './entryTypes'
import { findCatalogMatchByName } from './entryTypes'

// ─── Scripted "conversation replay" — ported from
// webapp/src/components/account/SubmissionChatView.tsx. Pure data logic, no UI. ───

const PostType: Record<string, string> = { call: 'chamada', text: 'mensagem/email', image: 'imagem' }
const PostTypePrefix: Record<string, string> = { call: 'o', text: 'o', image: 'a' }

const chatMessages = {
  step00: 'Bem vindo ao burla ou não',
  step0: 'Tens uma comunicação para partilhar? Qual o tipo?',
  step1_image: 'Segue as instruções abaixo.',
  step1_text: (type: string) => `Indica ${PostTypePrefix[type] ?? ''} nº ${PostType[type] ?? type}.`,
  step2: 'Qual o tipo de mensagem? Se não estiver na lista escreve em baixo.',
  step2Call: 'Qual o tipo de chamada? Se não estiver na lista escreve em baixo.',
  step3: 'E a plataforma? Se não estiver na lista escreve em baixo.',
  step3_1: 'Ou envia já a partilha.',
  finalstep: 'Clica enviar para submeteres',
}

const isClassifySuccessStep = (step: string) =>
  step === 'classify_success' || step === 'classify_retry_success' || step?.startsWith('classify_manual_retry_success_')

type ChatMessage = {
  id: string
  type: 'bot' | 'user' | 'infoCard'
  text?: string
  isLink?: boolean
  msgKey?: string
  isContent?: boolean
  from?: string
  subject?: string
  isThinking?: boolean
  validated?: boolean
  status?: 'success' | 'error'
  statusField?: 'verdict_label' | 'summary' | 'explanation' | 'recommendation'
  statusFieldIndex?: number
  rawField?: 'platform' | 'messageType' | 'callType'
  platformId?: number | string
  messageTypeId?: number | string
  callTypeId?: number | string
  platform?: string
  messageType?: string
  callType?: string
}

function buildMessages(rawEntry: any, pipelineSteps: any[], platforms: CatalogItem[], messageTypes: CatalogItem[], callTypes: CatalogItem[]): ChatMessage[] {
  const msgs: ChatMessage[] = []
  let idx = 0
  const push = (msg: Omit<ChatMessage, 'id'>) => msgs.push({ id: `msg-${idx++}`, ...msg })

  const pt: string = rawEntry.post_type ?? ''

  push({ type: 'bot', text: chatMessages.step00, isLink: true, msgKey: 'welcome0' })
  push({ type: 'bot', text: chatMessages.step0 })

  if (pt) push({ type: 'user', text: PostType[pt] ?? pt })

  if (pt === 'image') push({ type: 'bot', text: chatMessages.step1_image })
  else if (pt) push({ type: 'bot', text: chatMessages.step1_text(pt) })

  const isCall = pt === 'call'

  if (isCall) {
    if (rawEntry.sender) push({ type: 'user', text: rawEntry.sender })
  } else {
    const contentText = rawEntry.message_content ?? ''
    if (contentText || rawEntry.sender || rawEntry.message_subject) {
      push({ type: 'user', text: contentText, isContent: true, from: rawEntry.sender, subject: rawEntry.message_subject })
    }
  }

  push({ type: 'bot', text: isCall ? chatMessages.step2Call : chatMessages.step2 })
  const typeId = isCall ? rawEntry.call_type_id : rawEntry.message_type_id
  const typeText = isCall ? rawEntry.call_type_text : rawEntry.message_type_text
  if (typeId) {
    const collection = isCall ? callTypes : messageTypes
    const mt = collection.find((m) => String(m.id) === String(typeId))
    if (isCall) push({ type: 'user', callTypeId: typeId, text: mt?.name ?? '', rawField: 'callType' })
    else push({ type: 'user', messageTypeId: typeId, text: mt?.name ?? '', rawField: 'messageType' })
  } else if (typeText) {
    // No resolved *_id, but the free-text value might still match a known
    // catalog entry by name (e.g. ingest didn't link it) — try that before
    // falling back to a plain text bubble with no logo.
    const matched = findCatalogMatchByName(isCall ? callTypes : messageTypes, typeText)
    if (matched) {
      if (isCall) push({ type: 'user', callTypeId: matched.id, text: matched.name, rawField: 'callType' })
      else push({ type: 'user', messageTypeId: matched.id, text: matched.name, rawField: 'messageType' })
    } else if (isCall) {
      push({ type: 'user', callType: typeText, text: typeText, rawField: 'callType' })
    } else {
      push({ type: 'user', messageType: typeText, text: typeText, rawField: 'messageType' })
    }
  }

  push({ type: 'bot', text: chatMessages.step3 })
  push({ type: 'bot', text: chatMessages.step3_1 })
  if (rawEntry.platform_id) {
    const pl = platforms.find((p) => String(p.id) === String(rawEntry.platform_id))
    push({ type: 'user', platformId: rawEntry.platform_id, text: pl?.name ?? '', rawField: 'platform' })
  } else if (rawEntry.platform_text) {
    const matchedPlatform = findCatalogMatchByName(platforms, rawEntry.platform_text)
    if (matchedPlatform) push({ type: 'user', platformId: matchedPlatform.id, text: matchedPlatform.name, rawField: 'platform' })
    else push({ type: 'user', platform: rawEntry.platform_text, text: rawEntry.platform_text, rawField: 'platform' })
  }

  push({ type: 'bot', text: chatMessages.finalstep })
  push({ type: 'user', text: 'Enviado com sucesso.' })

  const createdAt = rawEntry.created_at || rawEntry.createdAt
  if (createdAt) push({ type: 'bot', text: `Submetido ${compactDate(createdAt)}` })

  if (pipelineSteps.length > 0) {
    const sortedClassifySteps = pipelineSteps
      .filter((s) => isClassifySuccessStep(s.step) || s.step?.startsWith('classify_manual_update'))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    const jsonInfo = sortedClassifySteps[0]?.json_info
    const translation: TranslationData | undefined =
      jsonInfo?.translation ?? sortedClassifySteps.find((s) => s.json_info?.translation)?.json_info?.translation

    if (!jsonInfo?.hide_classification) {
      const pipelineTrace: any[] = jsonInfo?.pipeline_trace ?? []
      pipelineTrace.forEach((step) => {
        if (step.description) push({ type: 'bot', text: step.description, isThinking: true, validated: true })
      })
    }

    // Never derive isScam from rawEntry.is_scam — only from the actual classify step,
    // since the raw value can be set before moderator review.
    const isScam: boolean | undefined = jsonInfo?.is_scam
    const status = isScam === true ? 'error' : isScam === false ? 'success' : undefined
    const hidden = jsonInfo?.hidden === true

    if (isScam === true) push({ type: 'bot', text: 'Esta comunicação foi classificada como burla.', status })
    else if (isScam === false) push({ type: 'bot', text: 'Esta comunicação não foi classificada como burla.', status })

    if (hidden) {
      push({ type: 'infoCard' })
    } else if (translation && !jsonInfo?.hide_classification) {
      if (translation.verdict_label) push({ type: 'bot', text: translation.verdict_label, status, statusField: 'verdict_label' })
      if (translation.summary) push({ type: 'bot', text: translation.summary, status, statusField: 'summary' })
      if (translation.explanation) push({ type: 'bot', text: translation.explanation, status, statusField: 'explanation' })
      ;(translation.recommendations ?? []).forEach((rec, i) =>
        push({ type: 'bot', text: `• ${rec}`, status, statusField: 'recommendation', statusFieldIndex: i }),
      )
    }
  }

  return msgs
}

// ─── Rendering ────────────────────────────────────────────────────────────────

function MessageIcon({ message, catalog }: { message: ChatMessage; catalog: Catalog }) {
  const logo =
    message.platformId !== undefined ? catalog.platforms.find((p) => String(p.id) === String(message.platformId))?.logo_url
    : message.callTypeId !== undefined ? catalog.callTypes.find((c) => String(c.id) === String(message.callTypeId))?.logo_url
    : message.messageTypeId !== undefined ? catalog.messageTypes.find((m) => String(m.id) === String(message.messageTypeId))?.logo_url
    : undefined

  if (logo) return <img src={logo} alt="" className="h-5 w-5 shrink-0 rounded-full object-contain" />
  if (message.platform || message.callType || message.messageType) {
    return <HelpCircle className="h-4 w-4 shrink-0 opacity-60" />
  }
  return null
}

function ChatBubble({
  message,
  theme,
  catalog,
  isEditable,
  isEditableRaw,
  isEditing,
  editingText,
  onStartEdit,
  onEditTextChange,
  onSave,
  onCancel,
  onEditRaw,
}: {
  message: ChatMessage
  theme: ProcessedTheme
  catalog: Catalog
  isEditable: boolean
  isEditableRaw: boolean
  isEditing: boolean
  editingText: string
  onStartEdit: () => void
  onEditTextChange: (v: string) => void
  onSave: () => void
  onCancel: () => void
  onEditRaw: () => void
}) {
  const c = watchClasses(theme)
  const [copied, setCopied] = useState(false)

  if (message.type === 'infoCard') {
    return (
      <div className={`mx-2 my-1.5 flex items-start gap-2 rounded-md border border-dashed px-3 py-2.5 border-border bg-secondary`}>
        <Info className={`mt-0.5 h-4 w-4 shrink-0 ${c.faint}`} />
        <span className={`text-[13px] ${c.muted}`}>
          Não há informação suficiente disponível para dar uma resposta com confiança. Em breve será possível submeter informação adicional.
        </span>
      </div>
    )
  }

  const isUser = message.type === 'user'
  const isAnyEditable = isEditable || isEditableRaw
  const disabled = !isAnyEditable || (isEditable && isEditing)

  const toneCls =
    message.status === 'error' ? ('bg-danger-surface border-destructive/40 text-destructive')
    : message.status === 'success' ? (theme === 'dark' ? 'bg-success-surface border-success/40 text-success' : 'bg-success-surface border-success/40 text-emerald-800')
    : isUser ? (theme === 'dark' ? 'bg-signal-surface border-signal/30 text-signal' : 'bg-signal-surface border-signal/30 text-sky-950')
    : message.isThinking ? ('bg-secondary border-border text-muted-foreground opacity-75')
    : ('bg-card border-border text-foreground')

  const text = message.isContent
    ? [message.from, message.subject, message.text].filter(Boolean).join('\n')
    : message.text

  const handleCopy = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (!text) return
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      {/* A plain div rather than a <button> — a native disabled <button> suppresses
          pointer events for its whole subtree, which would swallow clicks on the
          copy icon nested inside non-editable (i.e. usually disabled) bubbles. */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={() => { if (!disabled) (isEditableRaw ? onEditRaw() : isEditable && !isEditing ? onStartEdit() : undefined) }}
        onKeyDown={(e) => {
          if (disabled || (e.key !== 'Enter' && e.key !== ' ')) return
          e.preventDefault()
          isEditableRaw ? onEditRaw() : isEditable && !isEditing ? onStartEdit() : undefined
        }}
        className={`group my-0.5 max-w-[85%] border px-3 py-2 text-left text-[13px] leading-snug transition-colors ${toneCls} ${message.isThinking ? 'py-1.5 text-[12px]' : ''} ${isAnyEditable ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <div className="flex items-start gap-2">
          <MessageIcon message={message} catalog={catalog} />
          {isEditing ? (
            <div className="flex-1" onClick={(e) => e.stopPropagation()}>
              <textarea
                autoFocus
                value={editingText}
                onChange={(e) => onEditTextChange(e.target.value)}
                className="w-full resize-none rounded border border-white/20 bg-transparent p-1 text-[13px] outline-none"
                rows={2}
              />
              <div className="mt-1.5 flex justify-end gap-2">
                <button type="button" onClick={onSave} className="rounded p-0.5 text-success hover:bg-black/10">
                  <Check className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={onCancel} className="rounded p-0.5 text-destructive hover:bg-black/10">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <span className="whitespace-pre-wrap">
              {message.isLink && message.msgKey === 'welcome0' && text?.includes('burla ou não') ? (
                <>
                  {text.split('burla ou não')[0]}
                  <span className="underline">burla ou não</span>
                  {text.split('burla ou não')[1] || ''}
                </>
              ) : (
                text
              )}
            </span>
          )}
          {message.isThinking && <span className="ml-1 shrink-0 text-success">✓</span>}
          {!isEditing && isUser && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleCopy}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCopy(e) }}
              title="Copy"
              className={`ml-1 mt-0.5 h-3 w-3 shrink-0 cursor-pointer transition-opacity ${copied ? 'opacity-70' : 'opacity-0 group-hover:opacity-40 hover:opacity-70'}`}
            >
              {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3" />}
            </span>
          )}
          {!isEditing && isAnyEditable && (
            <Pencil className="ml-1 mt-0.5 h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-40" />
          )}
        </div>
      </div>
    </div>
  )
}

function FieldPicker({
  field,
  catalog,
  currentId,
  theme,
  onSelect,
  onClose,
}: {
  field: 'platform' | 'messageType' | 'callType'
  catalog: Catalog
  currentId?: string
  theme: ProcessedTheme
  onSelect: (id?: string, text?: string) => void
  onClose: () => void
}) {
  const c = watchClasses(theme)
  const entries = field === 'platform' ? catalog.platforms : field === 'callType' ? catalog.callTypes : catalog.messageTypes

  return (
    <div className={`border-t px-2 py-2 ${c.line}`}>
      <div className="mb-1.5 flex justify-end">
        <button type="button" onClick={onClose} className={c.faint}>
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {field === 'platform' && (
          <button
            type="button"
            onClick={() => onSelect(undefined, undefined)}
            className={`rounded-[4px] border px-2.5 py-1 text-[11px] ${c.line} ${c.muted} hover:border-signal`}
          >
            Limpar
          </button>
        )}
        {entries.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => onSelect(String(entry.id), entry.name)}
            className={`rounded-[4px] border px-2.5 py-1 text-[11px] transition-colors ${
              String(entry.id) === currentId ? 'border-signal bg-signal-surface text-signal' : `${c.line} ${c.muted} hover:border-signal`
            }`}
          >
            {entry.name}
          </button>
        ))}
      </div>
    </div>
  )
}

export function SubmissionChatView({
  submission,
  catalog,
  theme = 'light',
  onSaveClassification,
  onEditRawField,
}: {
  submission: any
  catalog: Catalog
  theme?: ProcessedTheme
  onSaveClassification?: (translation: TranslationData) => void
  onEditRawField?: (field: 'platform' | 'messageType' | 'callType', id?: string, text?: string) => void
}) {
  const rawEntry = submission.raw_entries ?? submission
  const pipelineSteps: any[] = submission.pipeline_steps ?? submission.user_entry_pipeline_steps ?? []
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingText, setEditingText] = useState('')
  const [activeEditField, setActiveEditField] = useState<'platform' | 'messageType' | 'callType' | null>(null)

  const currentTranslation: TranslationData | undefined = useMemo(() => {
    return pipelineSteps
      .filter((s) => isClassifySuccessStep(s.step) || s.step?.startsWith('classify_manual_update'))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]?.json_info?.translation
  }, [pipelineSteps])

  const handleSave = useCallback((msg: ChatMessage) => {
    if (!currentTranslation) return
    const updated: TranslationData = { ...currentTranslation }
    if (msg.statusField === 'verdict_label') updated.verdict_label = editingText
    else if (msg.statusField === 'summary') updated.summary = editingText
    else if (msg.statusField === 'explanation') updated.explanation = editingText
    else if (msg.statusField === 'recommendation' && msg.statusFieldIndex !== undefined) {
      const recs = [...(updated.recommendations ?? [])]
      recs[msg.statusFieldIndex] = editingText.replace(/^•\s*/, '')
      updated.recommendations = recs
    }
    onSaveClassification?.(updated)
    setEditingId(null)
  }, [currentTranslation, editingText, onSaveClassification])

  const msgs = useMemo(
    () => buildMessages(rawEntry, pipelineSteps, catalog.platforms, catalog.messageTypes, catalog.callTypes),
    [rawEntry, pipelineSteps, catalog],
  )

  const currentFieldId =
    activeEditField === 'platform' ? rawEntry.platform_id?.toString()
    : activeEditField === 'callType' ? rawEntry.call_type_id?.toString()
    : rawEntry.message_type_id?.toString()

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-1 overflow-y-auto p-3">
        {msgs.map((msg) => {
          const isEditable = !!onSaveClassification && !!msg.statusField
          const isEditableRaw = !!onEditRawField && !!msg.rawField
          const isEditing = editingId === msg.id
          return (
            <ChatBubble
              key={msg.id}
              message={msg}
              theme={theme}
              catalog={catalog}
              isEditable={isEditable}
              isEditableRaw={isEditableRaw}
              isEditing={isEditing}
              editingText={isEditing ? editingText : msg.text ?? ''}
              onStartEdit={() => { setEditingId(msg.id); setEditingText(msg.text ?? '') }}
              onEditTextChange={setEditingText}
              onSave={() => handleSave(msg)}
              onCancel={() => setEditingId(null)}
              onEditRaw={() => setActiveEditField(msg.rawField ?? null)}
            />
          )
        })}
      </div>
      {activeEditField && (
        <FieldPicker
          field={activeEditField}
          catalog={catalog}
          currentId={currentFieldId}
          theme={theme}
          onSelect={(id, text) => { onEditRawField?.(activeEditField, id, text); setActiveEditField(null) }}
          onClose={() => setActiveEditField(null)}
        />
      )}
    </div>
  )
}
